import React, { useState, useEffect } from 'react';
//Styles
import { useStyles } from 'components/page/AccountImportPage/AccountImportPage.style';
//Mui components
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
//PCP components
import AccountImportSelect from 'components/sheets/AccountImportSheet/AccountImportSelect';
import AccountImportMatchings from 'components/sheets/AccountImportSheet/AccountImportMatchings';
import AccountImportKeySelect from 'components/sheets/AccountImportSheet/AccountImportKeySelect';
import AccountImportFinalStep from 'components/sheets/AccountImportSheet/AccountImportFinalStep';
import AccountImportRules from 'components/sheets/AccountImportSheet/AccountImportRules';
// Locales
import translate from 'locales/i18n';
//Actions & Stores
import AccountStore from 'stores/AccountStore';
import AlertAction from 'actions/AlertAction';
import AttributeStore from 'stores/AttributeStore';
import AttributeAction from 'actions/AttributeAction';
// Tool
import { checkRequiredMatchings } from 'components/sheets/AccountImportSheet/AccountImport.utils';
import { ImportCSVTool } from 'tools/ImportCSVTool';
import Facade from 'core/Facade';

const AccountImportPage = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [selectedAccount, setSelectedAccount] = useState({});
    const [file, setFile] = useState({});
    const [errorMessages, setErrorMessages] = useState([]);
    const [showDialogConfirm, setShowDialogConfirm] = useState(false);
    const [importType, setImportType] = useState('account');

    const [matchings, setMatchings] = useState({});
    const [keys, setKeys] = useState([]);
    const [itemRules, setItemRules] = useState([{
        name: 'input_article_rule',
        value: 'CREATE',
        values: ['IGNORE', 'CREATE']
    }, {
        name: 'article_match_rule',
        value: 'UPDATE',
        values: ['IGNORE', 'DELETE', 'UPDATE']
    }, {
        name: 'spe_article_rule',
        value: 'IGNORE',
        values: ['IGNORE', 'DELETE']
    }]);
    const [attributeRules, setAttributeRules] = useState();
    const [headerAndSamples, setHeaderAndSamples] = useState({});
    const [accountDiffStats, setAccountDiffStats] = useState(null);
    const [keyUnique, setKeyUnique] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const classes = useStyles();

    const importCSVTool = new ImportCSVTool();

    const [steps, setSteps] = useState([
        translate.i18n('SELECT_ACCOUNT'),
        translate.i18n('MATCHING_HEADERS'),
        translate.i18n('SELECT_A_KEY'),
        translate.i18n('SET_ITEMS_RULES'),
        translate.i18n('IMPORT')
    ]);

    const [customAttributes, setCustomAttributes] = useState(AttributeStore.getAttributeNames());

    useEffect(() => {
        AttributeStore.addChangeListener(onAttributeStoreChange);
        return () => AttributeStore.removeChangeListener(onAttributeStoreChange);
    });

    useEffect(() => {
        if (!Facade.isEmpty(selectedAccount)) {
            if (importType === 'account') {
                AttributeAction.getAttributeNames(selectedAccount.id);
                setMatchings(importCSVTool.createMatchingsObject(selectedAccount, customAttributes));
                Facade.setUrlParam('account', selectedAccount.id);
                const newSteps = [
                    translate.i18n('SELECT_ACCOUNT'),
                    translate.i18n('MATCHING_HEADERS'),
                    translate.i18n('SELECT_A_KEY'),
                    translate.i18n('SET_ITEMS_RULES'),
                    selectedAccount?.name ? <div>{translate.i18n('IMPORT')}<span className={classes.accountName}>{selectedAccount.name}</span></div> : translate.i18n('IMPORT')
                ];
                setSteps(newSteps);
            } else {
                setMatchings(importCSVTool.createAdditionalDatasMatching());
                Facade.setUrlParam('account', selectedAccount.id);
                const newSteps = [
                    translate.i18n('SELECT_ACCOUNT'),
                    translate.i18n('MATCHING_HEADERS'),
                    translate.i18n('SELECT_A_KEY'),
                    selectedAccount?.name ? <div>{translate.i18n('IMPORT')}<span className={classes.accountName}>{selectedAccount.name}</span></div> : translate.i18n('IMPORT')
                ];
                setSteps(newSteps);
            }
        }
        importType === 'account' ? setAccountDiffStats(AccountStore.getAccountDiffStats()) : setAccountDiffStats(null);
    },[selectedAccount, importType]);

    useEffect(() => {
        if (!Facade.isEmpty(selectedAccount)) setMatchings(importCSVTool.createMatchingsObject(selectedAccount, customAttributes));
    }, [customAttributes]);

    const onAttributeStoreChange = () => {
        setCustomAttributes(AttributeStore.getAttributeNames());
    };

    // MATCHING LOGIC //

    const setMatching = (attribute, header) => {
        const newMatchs = matchings[attribute];
        newMatchs.header = header;
        newMatchs.errors = [];
        newMatchs.parsedData = [];
        setMatchings({ ...matchings, [attribute]: newMatchs });
    };

    const setNumberFormat = (attribute, numberFormat) => {
        const newMatchs = matchings[attribute];
        newMatchs.numberFormat = numberFormat;
        newMatchs.errors = [];
        setMatchings({ ...matchings, [attribute]: newMatchs });
    };

    const setFileAndResetMatching = (newFile) => {
        if (newFile) {
            const fileExtension = newFile.name.split('.').pop();
            if (importCSVTool.getSupportedFileExtension().includes(fileExtension)) {
                setFile(newFile);
                setErrorMessages([]);
                resetMatchingErrorsAndParsedData();
            } else {
                const newList = [];
                newList.push(translate.i18n('ERROR_FILE_TYPE_NOT_SUPPORTED'));
                setErrorMessages(newList);
                setFile({});
            }
        }
    };

    const resetMatchingErrorsAndParsedData = () => {
        const newMatch = matchings;
        if (!Facade.isEmpty(newMatch)) {
            Object.entries(newMatch).forEach(matching => {
                matching[1].errors = [];
                matching[1].parsedData = [];
            });
            setMatchings(newMatch);
        }
    };

    // IMPORT BUSSINESS LOGIC //
    const launchFirstImport = () => {
        const filerReader = new FileReader();
        filerReader.onerror = () => {
            const newList = errorMessages.slice();
            newList.push(translate.i18n('ERROR_IMPORT'));
            setErrorMessages(newList);
            setFile({});
        };
        filerReader.onloadstart = setIsLoading(true);
        filerReader.onload = getHeaderAndNavigate;
        filerReader.onloadend = setIsLoading(false);
        filerReader.readAsBinaryString(file);
    };

    const launchFullImport = () => {
        const filerReader = new FileReader();
        filerReader.onerror = () => {
            const newList = errorMessages.slice();
            newList.push(translate.i18n('ERROR_IMPORT'));
            setErrorMessages(newList);
            setFile({});
            setActiveStep(0);
        };
        filerReader.onloadstart = setIsLoading(true);
        filerReader.onload = parseAndLoadItem;
        filerReader.onloadend = setIsLoading(false);
        filerReader.readAsBinaryString(file);
    };

    const getHeaderAndNavigate = (e) => {
        const data = e.target.result;
        const parseData = importCSVTool.parseSheetAsJson(data);
        const duplicateHeader = importCSVTool.findDuplicateHeader(parseData);
        if (duplicateHeader.length === 0) {
            const headerAndSampleObj = importCSVTool.getHeaderAndSampleFromParseData(parseData);
            setHeaderAndSamples(headerAndSampleObj);
            setActiveStep(activeStep + 1);
        } else {
            const newList = errorMessages.slice();
            newList.push(translate.i18n('ERROR_IMPORT_DUPLICATE_HEADER'));
            setErrorMessages(newList);
        }
    };

    const parseAndLoadItem = (e) => {
        const data = e.target.result;
        const parseData = importCSVTool.parseSheetAsJson(data);
        const headerAndData = importCSVTool.getHeaderAndDataFromParseData(parseData);

        const newMatchs = matchings;
        Object.entries(newMatchs).forEach(matching => {
            const errors = [];
            if (matching[1].header === '' && matching[1].required) {
                errors.push(translate.i18n('ATTRIBUTE_REQUIRED'));
            }
            matching[1].errors = errors;
        });
        Object.entries(newMatchs).forEach(matching => {
            const column = headerAndData[matching[1].header];
            if (!Facade.isEmpty(column)) {
                let parsedColumn = [];
                let errors = [];
                let parsedColumnResult;
                switch (matching[1].type) {
                case 'Int':
                    parsedColumnResult = importCSVTool.parseColumnAsInt(column, matching[1].required, matching[1].numberFormat);
                    break;
                case 'Double':
                    parsedColumnResult = importCSVTool.parseColumnAsFloat(column, matching[1].required, matching[1].numberFormat);
                    break;
                case 'String':
                    parsedColumnResult = importCSVTool.parseColumnAsString(column, matching[1].required);
                    break;
                default: // Site URL Case
                    parsedColumnResult = importCSVTool.parseColumnAsSiteURL(column, matching[1].required, matching[1].type);
                }
                ({ parsedColumn } = parsedColumnResult);
                ({ errors } = parsedColumnResult);
                console.log(errors);
                matching[1].parsedData = parsedColumn;
                matching[1].errors = errors;
            }
        });

        // Remove space in category name
        if (newMatchs.category && newMatchs.category.parsedData) newMatchs.category.parsedData = newMatchs.category.parsedData.map(c => c && c.trim());

        setMatchings(newMatchs);
        if (checkRequiredMatchings(matchings)) {
            if (importType === 'additionalData' && Object.entries(matchings).some(match => Facade.isEmpty(match[1].parsedData))) {
                setShowDialogConfirm(true);
            } else {
                if (!importCSVTool.parsingHasError(newMatchs)) {
                    setActiveStep(activeStep + 1);
                }
            }
        } else {
            AlertAction.open('error', 'Matching error', 'top-right');
        }
    };

    // NAVIGATION LOGIC //
    const handleNext = () => {

        switch (activeStep) {
        case 0:
            if (Facade.isEmpty(file) && (Facade.isEmpty(selectedAccount) || !selectedAccount.modules.spe)) {
                const newList = [];
                newList.push(translate.i18n('PLEASE_ADD_FILE'));
                newList.push(translate.i18n('PLEASE_SELECT_ACCOUNT'));
                setErrorMessages(newList);
            } else if (Facade.isEmpty(file)) {
                const newList = [];
                newList.push(translate.i18n('PLEASE_ADD_FILE'));
                setErrorMessages(newList);
            } else if (Facade.isEmpty(selectedAccount) || !selectedAccount.modules.spe) {
                const newList = [];
                newList.push(translate.i18n('PLEASE_SELECT_ACCOUNT'));
                setErrorMessages(newList);
            } else {
                launchFirstImport();
            }
            break;
        case 1:
            launchFullImport();
            break;
        case 2:
            importType === 'account' && setAttributeRules(importCSVTool.createAttributeRulesObject(matchings, keys));
            importType === 'account' ? setActiveStep(activeStep + 1) : setActiveStep(activeStep + 2);
            break;
        default:
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {

        switch (activeStep) {
        case 2:
            setActiveStep(activeStep - 1);
            setKeys([]);
            setKeyUnique(false);
            setAccountDiffStats(null);
            break;
        case 4:
            importType === 'account' ? setActiveStep(activeStep - 1) : setActiveStep(activeStep - 2);
            break;
        default:
            setActiveStep(activeStep - 1);
        }
    };

    return (
        <div className={classes.root}>
            <Paper variant='outlined' square className={classes.paper}>
                <Stepper activeStep={activeStep} alternativeLabel style={{ padding: '14px' }}>
                    {steps.map((label) =>
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    )}
                </Stepper>
                {isLoading ?
                    <div className={classes.loaderContainer}><CircularProgress /></div>
                    :
                    <>
                        {activeStep === 0 &&
                            <AccountImportSelect
                                selectedAccount={selectedAccount}
                                file={file}
                                setFileAndResetMatching={setFileAndResetMatching}
                                setSelectedAccount={setSelectedAccount}
                                importType={importType}
                                setImportType={setImportType}
                                errorMessages={errorMessages}/>
                        }

                        {activeStep === 1 &&
                            <AccountImportMatchings
                                matchings={matchings}
                                setMatchings={setMatchings}
                                headerAndSamples={headerAndSamples}
                                setMatching={setMatching}
                                setNumberFormat={setNumberFormat}
                                showDialogConfirm={showDialogConfirm}
                                setShowDialogConfirm={setShowDialogConfirm}
                                setActiveStep={setActiveStep}
                                importType={importType}
                            />
                        }
                        {activeStep === 2 &&
                            <AccountImportKeySelect
                                keys={keys}
                                selectedAccount={selectedAccount}
                                accountDiffStats={accountDiffStats}
                                setAccountDiffStats={setAccountDiffStats}
                                keyUnique={keyUnique}
                                setKeyUnique={setKeyUnique}
                                matchings={matchings}
                                setKeys={setKeys}
                                importType={importType}
                            />
                        }
                        {activeStep === 3 &&
                            <AccountImportRules
                                itemRules={itemRules}
                                attributeRules={attributeRules}
                                setItemRules={setItemRules}
                                setAttributeRules={setAttributeRules}
                            />
                        }
                        {activeStep === 4 &&
                            <AccountImportFinalStep
                                selectedAccount={selectedAccount}
                                keys={keys}
                                itemRules={itemRules}
                                attributeRules={attributeRules}
                                matchings={matchings}
                                importType={importType}
                            />
                        }
                    </>

                }
                <div className={classes.stepperButtons}>
                    <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        className={classes.backButton}
                        align='center'
                    >
                        {translate.i18n('BACK')}
                    </Button>
                    {activeStep !== 4 &&
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={handleNext}
                            align='center'
                            disabled={(activeStep === 2 && !accountDiffStats && !keyUnique)}
                        >
                            {translate.i18n('NEXT')}
                        </Button>
                    }
                </div>
            </Paper>
        </div>
    );
};

export default AccountImportPage;
